import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Smartphone, X, Share } from "lucide-react";

const DISMISS_KEY = "chalann_install_dismissed_at";
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 días

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

/**
 * Small dismissible card that:
 *  - On Android/desktop Chrome captures `beforeinstallprompt` and lets the user install natively.
 *  - On iOS Safari (which lacks the event) shows step-by-step instructions.
 * Persists dismissal for 7 días.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_TTL_MS) return;

    const onBip = (e) => {
      e.preventDefault();
      // Re-check TTL inside handler in case beforeinstallprompt re-fires within the same session.
      const dAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (dAt && Date.now() - dAt < DISMISS_TTL_MS) return;
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    // iOS Safari never fires beforeinstallprompt. Show fallback card.
    if (isIOS()) setVisible(true);

    const onInstalled = () => {
      setVisible(false);
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
    setShowIOSHelp(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        deferred.prompt();
        const { outcome } = await deferred.userChoice;
        if (outcome === "accepted") setVisible(false);
      } catch { /* noop */ }
      setDeferred(null);
      return;
    }
    if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -20, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative mb-4 border border-[#ff5a00]/30 bg-gradient-to-r from-[#ff5a00]/10 via-[#0a0a0a] to-[#0a0a0a] rounded-2xl p-4 md:p-5"
        data-testid="install-prompt"
      >
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 text-white/40 hover:text-white p-1 rounded-full"
          aria-label="Cerrar"
          data-testid="install-prompt-dismiss"
        >
          <X size={16} />
        </button>

        {!showIOSHelp ? (
          <button
            onClick={install}
            className="w-full flex items-start gap-4 text-left pr-6 group"
            data-testid="install-prompt-cta"
          >
            <span className="mt-1 w-10 h-10 rounded-xl bg-[#ff5a00] text-black flex items-center justify-center flex-shrink-0 shadow-[0_10px_30px_-10px_rgba(255,90,0,0.6)]">
              <Smartphone size={20} strokeWidth={2.5} />
            </span>
            <span className="flex-1">
              <span className="block font-display text-lg md:text-xl tracking-tighter leading-tight">
                ¡Fija tu asistente en tu escritorio!
              </span>
              <span className="block text-white/60 text-sm mt-1 leading-snug">
                Toca aquí para agregar <span className="text-[#ccff00]">&ldquo;Chalan&rdquo;</span> a la pantalla de tu celular como una app normal.
              </span>
            </span>
          </button>
        ) : (
          <div className="pr-6" data-testid="install-prompt-ios">
            <p className="font-display text-lg tracking-tighter mb-2">Instalar en iPhone</p>
            <ol className="space-y-1.5 text-sm text-white/70 list-decimal list-inside">
              <li>Toca el botón <Share size={12} className="inline mx-1 text-[#ccff00]" /> Compartir de Safari.</li>
              <li>Elige <span className="text-white">&ldquo;Agregar a inicio&rdquo;</span>.</li>
              <li>Confirma con <span className="text-white">&ldquo;Agregar&rdquo;</span>. Listo, ya tienes tu Chalan.</li>
            </ol>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
